import {
    SimpleAndFilter,
    SimpleOrFilter,
} from "@impero-com/common/utils/indexing";

import {
    ControlProgramsFilters,
    ControlProgramsColumn,
    PerCategoryFilter,
    StandardAccessRight,
} from "api/generated/model";
import { CP_EXTRACTOR_KEYS } from "org/control/controlProgram/extractorKeys";
import { PageStateData, IdAndName, OrFilter } from "org/pageState/models";
import { Codecs, PageFilterAndColumns } from "org/pageState/types";
import { unwrapPageStateAs } from "org/pageState/utils";
import {
    accessRightCategoryExtractorId,
    ALL_ACCESS_RIGHTS,
} from "utils/accessRights";
import { DEFAULT_CATEGORY_METHODS } from "utils/tag";

import { decodeOrFilter, encodeMatchRule, encodeOrFilter } from "./common";
import { createEncodeColumnFn } from "./controls/common";

// Keep it in sync with the definition in impero_shareable_links/src/api/page_state/control_programs.rs
const CONTROL_PROGRAMS_ALL_COLUMNS: ControlProgramsColumn[] = [
    CP_EXTRACTOR_KEYS.NAME,
    CP_EXTRACTOR_KEYS.CONTROL_COUNT,
    CP_EXTRACTOR_KEYS.STATUS,
    CP_EXTRACTOR_KEYS.ACCESS_RIGHTS,
    CP_EXTRACTOR_KEYS.TAGS,
    CP_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT,
    CP_EXTRACTOR_KEYS.ENTITIES,
];

const encodeControlProgramsColumn: (col: string) => ControlProgramsColumn =
    createEncodeColumnFn(CONTROL_PROGRAMS_ALL_COLUMNS);

// Filters

function encodeControlProgramsPageFilters(
    filters: Array<SimpleOrFilter>
): ControlProgramsFilters {
    return {
        name: encodeOrFilter(CP_EXTRACTOR_KEYS.NAME, filters),
        controlCount: encodeOrFilter(CP_EXTRACTOR_KEYS.CONTROL_COUNT, filters),
        status: encodeOrFilter(CP_EXTRACTOR_KEYS.STATUS, filters),
        tagCategories: encodeOrFilter(
            CP_EXTRACTOR_KEYS.TAG_CATEGORIES,
            filters
        ),
        tags: encodeTags(filters),
        accessRights: encodeAccessRights(filters),
        controlResultCount: encodeOrFilter(
            CP_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT,
            filters
        ),
        entities: encodeOrFilter(CP_EXTRACTOR_KEYS.ENTITIES, filters),
    };
}

function decodeControlProgramsPageFilters(
    filters: ControlProgramsFilters
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];
    if (filters.name) {
        output.push(decodeOrFilter(CP_EXTRACTOR_KEYS.NAME, filters.name));
    }
    if (filters.controlCount) {
        output.push(
            decodeOrFilter(
                CP_EXTRACTOR_KEYS.CONTROL_COUNT,
                filters.controlCount
            )
        );
    }
    if (filters.status) {
        output.push(decodeOrFilter(CP_EXTRACTOR_KEYS.STATUS, filters.status));
    }
    if (filters.tagCategories) {
        output.push(
            decodeOrFilter(
                CP_EXTRACTOR_KEYS.TAG_CATEGORIES,
                filters.tagCategories
            )
        );
    }
    if (filters.tags) {
        const tagFilters = decodeTags(filters.tags);
        output.push(...tagFilters);
    }
    if (filters.accessRights) {
        const accessRightFilters = decodeAccessRights(filters.accessRights);
        output.push(...accessRightFilters);
    }
    if (filters.controlResultCount) {
        output.push(
            decodeOrFilter(
                CP_EXTRACTOR_KEYS.CONTROL_RESULT_COUNT,
                filters.controlResultCount
            )
        );
    }
    if (filters.entities) {
        output.push(
            decodeOrFilter(CP_EXTRACTOR_KEYS.ENTITIES, filters.entities)
        );
    }
    return output;
}

function decodeTags(
    tagFiltersPerCategoryId: Record<string, PerCategoryFilter>
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];

    for (const [catId, perCategoryFilter] of Object.entries(
        tagFiltersPerCategoryId
    )) {
        const { categoryName, filter } = perCategoryFilter;
        const extractorId: string =
            DEFAULT_CATEGORY_METHODS.abstractTagCategoryToExtractorId({
                id: catId,
                name: categoryName,
            });
        output.push(decodeOrFilter(extractorId, filter));
    }

    return output;
}

function encodeTags(
    filters: Array<SimpleOrFilter>
): Record<string, PerCategoryFilter> {
    const output: Record<string, PerCategoryFilter> = {};

    const categoryFilters = filters.filter((f) =>
        DEFAULT_CATEGORY_METHODS.isCategoryExtractorId(f.extractorId)
    );
    for (const categoryFilter of categoryFilters) {
        const { id } =
            DEFAULT_CATEGORY_METHODS.extractorIdToAbstractTagCategory(
                categoryFilter.extractorId
            );
        const encodedFilter = encodePerCategoryFilter(
            categoryFilter.extractorId,
            filters
        );

        if (encodedFilter) {
            output[id] = encodedFilter;
        }
    }

    return output;
}

function encodePerCategoryFilter(
    extractorId: string,
    filters: Array<SimpleOrFilter>
): PerCategoryFilter | undefined {
    const relevantFilter = filters.find(
        (f) => f.extractorId === extractorId
    ) as SimpleOrFilter<IdAndName<string, string>> | undefined;
    if (!relevantFilter) {
        return undefined;
    }

    const includeFilters = relevantFilter.filters ? relevantFilter.filters : [];
    const excludeFilters = relevantFilter.exclusionFilters
        ? relevantFilter.exclusionFilters
        : [];

    const { name } =
        DEFAULT_CATEGORY_METHODS.extractorIdToAbstractTagCategory(extractorId);

    const filter: OrFilter<IdAndName<string, string>> = {
        include: includeFilters.map(encodeMatchRule),
        exclude: excludeFilters.map(encodeMatchRule),
    };

    return { categoryName: name, filter };
}

function encodeAccessRights(
    filters: Array<SimpleOrFilter>
): Partial<
    Record<StandardAccessRight, OrFilter<IdAndName<number, string>> | undefined>
> {
    const encodeRight = (right: StandardAccessRight) =>
        encodeOrFilter<IdAndName<number, string>>(
            accessRightCategoryExtractorId(right),
            filters
        );

    const output: Partial<
        Record<
            StandardAccessRight,
            OrFilter<IdAndName<number, string>> | undefined
        >
    > = {};
    ALL_ACCESS_RIGHTS.forEach((right) => {
        output[right] = encodeRight(right);
    });

    return output;
}

function decodeAccessRights(
    accessRights: Partial<
        Record<StandardAccessRight, OrFilter<IdAndName<number, string>>>
    >
): Array<SimpleOrFilter> {
    const output: Array<SimpleOrFilter> = [];

    ALL_ACCESS_RIGHTS.forEach((right) => {
        const orFilter = accessRights[right];
        if (orFilter !== undefined) {
            const extractorId = accessRightCategoryExtractorId(right);
            output.push(
                decodeOrFilter<IdAndName<number, string>>(extractorId, orFilter)
            );
        }
    });

    return output;
}

// Page state

type ControlProgramsPageStateWithType = PageStateData<"ControlPrograms"> & {
    type: "ControlPrograms";
};

function decodeControlProgramsPageState(
    pageState: ControlProgramsPageStateWithType
): PageFilterAndColumns {
    const unwrapped = unwrapPageStateAs(pageState, "ControlPrograms");
    const filter: SimpleAndFilter = {
        filters: decodeControlProgramsPageFilters(unwrapped.filters),
    };
    const visibleColumnIds = new Set(unwrapped.columns);
    return {
        pageType: pageState.type,
        filter,
        visibleColumnIds,
        version: pageState.version ?? 0,
    };
}

function encodeControlProgramsPageState(
    filterAndColumns: PageFilterAndColumns
): ControlProgramsPageStateWithType {
    const { filter, visibleColumnIds } = filterAndColumns;

    let filters: Array<SimpleOrFilter> = [];
    if (filter) {
        filters = filter.filters;
    }

    return {
        type: "ControlPrograms",
        filters: encodeControlProgramsPageFilters(filters),
        columns: Array.from(visibleColumnIds).map(encodeControlProgramsColumn),
        version: filterAndColumns.version,
    };
}

const pageStateControlProgramsCodecs: Codecs<
    PageFilterAndColumns,
    ControlProgramsPageStateWithType
> = {
    encode: encodeControlProgramsPageState,
    decode: decodeControlProgramsPageState,
};

export {
    encodeControlProgramsPageState,
    decodeControlProgramsPageState,
    pageStateControlProgramsCodecs,
    CONTROL_PROGRAMS_ALL_COLUMNS,
};
